import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subMonths, subQuarters, subYears } from 'date-fns';
import { supabase } from '../../lib/supabase';

const inputClass = 'bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-darker text-sm focus:outline-none focus:border-yellow duration-300';

export default function SalesReports() {
     const now = new Date();
     const [p1From, setP1From] = useState(format(startOfMonth(now), 'yyyy-MM-dd'));
     const [p1To, setP1To] = useState(format(endOfMonth(now), 'yyyy-MM-dd'));
     const [p2From, setP2From] = useState(format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'));
     const [p2To, setP2To] = useState(format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'));
     const [loading, setLoading] = useState(true);
     const [p1Data, setP1Data] = useState({ bookings: [], expenses: [] });
     const [p2Data, setP2Data] = useState({ bookings: [], expenses: [] });
     const [monthlyRevenue, setMonthlyRevenue] = useState([]);

     useEffect(() => {
          fetchComparison();
          fetchMonthlyTrend();
          // eslint-disable-next-line
     }, [p1From, p1To, p2From, p2To]);

     async function fetchComparison() {
          setLoading(true);
          const [b1, e1, b2, e2] = await Promise.all([
               supabase.from('bookings').select('*').in('status', ['confirmed', 'completed']).gte('start_date', p1From).lte('start_date', p1To),
               supabase.from('expenses').select('*').gte('date', p1From).lte('date', p1To),
               supabase.from('bookings').select('*').in('status', ['confirmed', 'completed']).gte('start_date', p2From).lte('start_date', p2To),
               supabase.from('expenses').select('*').gte('date', p2From).lte('date', p2To),
          ]);
          setP1Data({ bookings: b1.data || [], expenses: e1.data || [] });
          setP2Data({ bookings: b2.data || [], expenses: e2.data || [] });
          setLoading(false);
     }

     async function fetchMonthlyTrend() {
          const months = [];
          for (let i = 11; i >= 0; i--) {
               const d = subMonths(now, i);
               months.push({
                    label: format(d, 'MMM yyyy'),
                    from: format(startOfMonth(d), 'yyyy-MM-dd'),
                    to: format(endOfMonth(d), 'yyyy-MM-dd'),
               });
          }

          const { data } = await supabase
               .from('bookings')
               .select('start_date, total_cents')
               .in('status', ['confirmed', 'completed'])
               .gte('start_date', months[0].from)
               .lte('start_date', months[11].to);

          const bookings = data || [];
          const result = months.map(m => {
               const rev = bookings
                    .filter(b => b.start_date >= m.from && b.start_date <= m.to)
                    .reduce((s, b) => s + (b.total_cents || 0), 0);
               return { label: m.label, revenue: rev };
          });
          setMonthlyRevenue(result);
     }

     function applyPreset(preset) {
          if (preset === 'month') {
               setP1From(format(startOfMonth(now), 'yyyy-MM-dd'));
               setP1To(format(endOfMonth(now), 'yyyy-MM-dd'));
               setP2From(format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'));
               setP2To(format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'));
          } else if (preset === 'quarter') {
               setP1From(format(startOfQuarter(now), 'yyyy-MM-dd'));
               setP1To(format(endOfQuarter(now), 'yyyy-MM-dd'));
               const lastQ = subQuarters(now, 1);
               setP2From(format(startOfQuarter(lastQ), 'yyyy-MM-dd'));
               setP2To(format(endOfQuarter(lastQ), 'yyyy-MM-dd'));
          } else if (preset === 'year') {
               setP1From(format(startOfYear(now), 'yyyy-MM-dd'));
               setP1To(format(endOfYear(now), 'yyyy-MM-dd'));
               const lastY = subYears(now, 1);
               setP2From(format(startOfYear(lastY), 'yyyy-MM-dd'));
               setP2To(format(endOfYear(lastY), 'yyyy-MM-dd'));
          }
     }

     function calcStats(data) {
          const revenue = data.bookings.reduce((s, b) => s + (b.total_cents || 0), 0);
          const expenseTotal = data.expenses.reduce((s, e) => s + (e.amount_cents || 0), 0);
          const avg = data.bookings.length ? Math.round(revenue / data.bookings.length) : 0;
          return { revenue, bookings: data.bookings.length, avg, expenses: expenseTotal, net: revenue - expenseTotal };
     }

     function pctChange(current, previous) {
          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / Math.abs(previous)) * 100;
     }

     function ChangeIndicator({ current, previous, inverse = false }) {
          const pct = pctChange(current, previous);
          if (pct === 0) return <span className='text-darker/40 text-xs'>--</span>;
          const improved = inverse ? pct < 0 : pct > 0;
          return (
               <span className={`text-xs font-medium ${improved ? 'text-green-400' : 'text-red-400'}`}>
                    {improved ? '\u2191' : '\u2193'} {Math.abs(pct).toFixed(1)}%
               </span>
          );
     }

     const s1 = calcStats(p1Data);
     const s2 = calcStats(p2Data);

     // Equipment breakdown comparison
     const equipMap = {};
     p1Data.bookings.forEach(b => {
          const key = b.equipment_description || 'Unknown';
          if (!equipMap[key]) equipMap[key] = { name: key, p1: 0, p2: 0 };
          equipMap[key].p1 += b.total_cents || 0;
     });
     p2Data.bookings.forEach(b => {
          const key = b.equipment_description || 'Unknown';
          if (!equipMap[key]) equipMap[key] = { name: key, p1: 0, p2: 0 };
          equipMap[key].p2 += b.total_cents || 0;
     });
     const equipList = Object.values(equipMap).sort((a, b) => (b.p1 + b.p2) - (a.p1 + a.p2));

     const fmt = (cents) => `$${(cents / 100).toLocaleString()}`;

     const maxMonthly = Math.max(...monthlyRevenue.map(m => m.revenue), 1);

     const statRows = [
          { label: 'Revenue', k: 'revenue', isCurrency: true },
          { label: 'Bookings', k: 'bookings', isCurrency: false },
          { label: 'Avg Order Value', k: 'avg', isCurrency: true },
          { label: 'Expenses', k: 'expenses', isCurrency: true, inverse: true },
          { label: 'Net Profit', k: 'net', isCurrency: true },
     ];

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Sales Reports</h1>

               {/* Preset Buttons */}
               <div className='flex flex-wrap gap-2 mb-6'>
                    <button onClick={() => applyPreset('month')} className='bg-gray-50 text-darker/60 hover:text-darker px-3 py-1.5 rounded-lg text-sm font-medium duration-300'>
                         This Month vs Last Month
                    </button>
                    <button onClick={() => applyPreset('quarter')} className='bg-gray-50 text-darker/60 hover:text-darker px-3 py-1.5 rounded-lg text-sm font-medium duration-300'>
                         This Quarter vs Last Quarter
                    </button>
                    <button onClick={() => applyPreset('year')} className='bg-gray-50 text-darker/60 hover:text-darker px-3 py-1.5 rounded-lg text-sm font-medium duration-300'>
                         This Year vs Last Year
                    </button>
               </div>

               {/* Date Range Selectors */}
               <div className='grid grid-cols-1 tablet:grid-cols-2 gap-4 mb-6'>
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h2 className='text-darker text-sm font-semibold mb-3'>Period 1</h2>
                         <div className='flex flex-wrap items-center gap-3'>
                              <label className='text-darker/40 text-sm'>From</label>
                              <input type='date' value={p1From} onChange={e => setP1From(e.target.value)} className={inputClass} />
                              <label className='text-darker/40 text-sm'>To</label>
                              <input type='date' value={p1To} onChange={e => setP1To(e.target.value)} className={inputClass} />
                         </div>
                    </div>
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h2 className='text-darker text-sm font-semibold mb-3'>Period 2</h2>
                         <div className='flex flex-wrap items-center gap-3'>
                              <label className='text-darker/40 text-sm'>From</label>
                              <input type='date' value={p2From} onChange={e => setP2From(e.target.value)} className={inputClass} />
                              <label className='text-darker/40 text-sm'>To</label>
                              <input type='date' value={p2To} onChange={e => setP2To(e.target.value)} className={inputClass} />
                         </div>
                    </div>
               </div>

               {loading ? (
                    <p className='text-darker/60'>Loading report data...</p>
               ) : (
                    <>
                         {/* Side-by-Side Comparison */}
                         <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                              <h2 className='text-darker text-lg font-semibold mb-4'>Period Comparison</h2>
                              <div className='overflow-x-auto'>
                                   <table className='w-full text-sm'>
                                        <thead>
                                             <tr className='border-b border-gray-200'>
                                                  <th className='text-darker/40 text-left py-2 pr-4'>Metric</th>
                                                  <th className='text-darker/40 text-right py-2 px-4'>Period 1</th>
                                                  <th className='text-darker/40 text-right py-2 px-4'>Period 2</th>
                                                  <th className='text-darker/40 text-right py-2 pl-4'>Change</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {statRows.map(row => (
                                                  <tr key={row.k} className='border-b border-gray-200/50'>
                                                       <td className='text-darker py-3 pr-4'>{row.label}</td>
                                                       <td className='text-yellow text-right py-3 px-4 font-semibold'>
                                                            {row.isCurrency ? fmt(s1[row.k]) : s1[row.k]}
                                                       </td>
                                                       <td className='text-darker/60 text-right py-3 px-4'>
                                                            {row.isCurrency ? fmt(s2[row.k]) : s2[row.k]}
                                                       </td>
                                                       <td className='text-right py-3 pl-4'>
                                                            <ChangeIndicator current={s1[row.k]} previous={s2[row.k]} inverse={row.inverse} />
                                                       </td>
                                                  </tr>
                                             ))}
                                        </tbody>
                                   </table>
                              </div>
                         </div>

                         {/* Equipment Breakdown Comparison */}
                         <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                              <h2 className='text-darker text-lg font-semibold mb-4'>Revenue Breakdown by Equipment</h2>
                              {equipList.length === 0 ? (
                                   <p className='text-darker/40 text-sm'>No data</p>
                              ) : (
                                   <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                             <thead>
                                                  <tr className='border-b border-gray-200'>
                                                       <th className='text-darker/40 text-left py-2 pr-4'>Equipment</th>
                                                       <th className='text-darker/40 text-right py-2 px-4'>Period 1</th>
                                                       <th className='text-darker/40 text-right py-2 px-4'>Period 2</th>
                                                       <th className='text-darker/40 text-right py-2 pl-4'>Change</th>
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {equipList.map(item => (
                                                       <tr key={item.name} className='border-b border-gray-200/50'>
                                                            <td className='text-darker py-3 pr-4 truncate max-w-xs'>{item.name}</td>
                                                            <td className='text-yellow text-right py-3 px-4 font-semibold'>{fmt(item.p1)}</td>
                                                            <td className='text-darker/60 text-right py-3 px-4'>{fmt(item.p2)}</td>
                                                            <td className='text-right py-3 pl-4'>
                                                                 <ChangeIndicator current={item.p1} previous={item.p2} />
                                                            </td>
                                                       </tr>
                                                  ))}
                                             </tbody>
                                        </table>
                                   </div>
                              )}
                         </div>

                         {/* Monthly Revenue Trend - Bar Chart */}
                         <div className='bg-white rounded-xl p-5 border border-gray-200'>
                              <h2 className='text-darker text-lg font-semibold mb-4'>Monthly Revenue Trend (Last 12 Months)</h2>
                              <div className='flex items-end gap-2 h-48'>
                                   {monthlyRevenue.map((m, i) => {
                                        const height = maxMonthly > 0 ? (m.revenue / maxMonthly) * 100 : 0;
                                        return (
                                             <div key={i} className='flex-1 flex flex-col items-center justify-end h-full'>
                                                  <p className='text-darker/60 text-xs mb-1 whitespace-nowrap'>
                                                       {m.revenue > 0 ? fmt(m.revenue) : ''}
                                                  </p>
                                                  <div
                                                       className='w-full bg-yellow rounded-t-md min-h-[2px] duration-300'
                                                       style={{ height: `${Math.max(height, 1)}%` }}
                                                  />
                                                  <p className='text-darker/40 text-xs mt-2 whitespace-nowrap rotate-[-45deg] origin-top-left translate-y-2'>
                                                       {m.label.slice(0, 3)}
                                                  </p>
                                             </div>
                                        );
                                   })}
                              </div>
                              <div className='h-8' />
                         </div>
                    </>
               )}
          </div>
     );
}
